import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Building2, KeyRound, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/input";
import { orpc, queryClient } from "../lib/api";
import { authClient, followAuthRedirect } from "../lib/auth-client";
import { rememberOrg, signOut, useSession } from "../lib/session";

const SCOPE_LABELS: Record<string, string> = {
  openid: "Deine Identität bestätigen",
  profile: "Deinen Namen und dein Profilbild sehen",
  email: "Deine E-Mail-Adresse sehen",
  offline_access: "Verbunden bleiben, ohne dass du dich jedes Mal neu anmeldest",
  "mcp:read": "Organisationen, Projekte, Dateien und Medien lesen",
  "mcp:write": "Projekte und Dateien in deinem Namen anlegen und ändern",
};

/** OAuth consent screen, e.g. when ChatGPT or Claude connects to the MCP server. */
export function ConsentPage() {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("client_id") ?? "";
  const scopes = (params.get("scope") ?? "").split(" ").filter(Boolean);
  const { data: session } = useSession();
  const [busy, setBusy] = useState<"accept" | "deny" | null>(null);
  const client = useQuery({
    queryKey: ["oauth-client", clientId],
    queryFn: async () => {
      const { data } = await authClient.oauth2.publicClient({ query: { client_id: clientId } });
      return data;
    },
    enabled: Boolean(clientId),
  });
  const clientName = client.data?.client_name ?? "Eine externe App";

  async function decide(accept: boolean) {
    setBusy(accept ? "accept" : "deny");
    const { data, error } = await authClient.oauth2.consent({
      accept,
      scope: accept ? scopes.join(" ") : undefined,
    });
    if (error) {
      setBusy(null);
      toast.error(error.message ?? "Das hat nicht geklappt");
      return;
    }
    if (!followAuthRedirect(data)) setBusy(null);
  }

  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
        <ShieldCheck className="size-6" />
      </div>
      <h1 className="text-xl font-semibold text-zinc-900">{clientName} verbinden?</h1>
      <p className="mt-1 text-sm text-zinc-500">
        {clientName} möchte auf dein Konto{session ? ` (${session.user.email})` : ""} zugreifen und
        darf dann:
      </p>
      <ul className="mt-5 space-y-2 text-left">
        {scopes.map((scope) => (
          <li
            key={scope}
            className="flex items-start gap-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm text-zinc-700"
          >
            <KeyRound className="mt-0.5 size-4 shrink-0 text-brand-600" />
            {SCOPE_LABELS[scope] ?? scope}
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-zinc-500">
        Die App handelt mit deinen Rechten. Du kannst den Zugriff jederzeit unter „Konto →
        Verbundene Apps“ widerrufen.
      </p>
      <div className="mt-6 flex gap-2">
        <Button
          variant="secondary"
          className="flex-1"
          loading={busy === "deny"}
          disabled={busy !== null}
          onClick={() => decide(false)}
        >
          Ablehnen
        </Button>
        <Button
          className="flex-1"
          loading={busy === "accept"}
          disabled={busy !== null}
          onClick={() => decide(true)}
        >
          Erlauben
        </Button>
      </div>
      {session ? (
        <button
          type="button"
          onClick={() => signOut()}
          className="mt-5 text-xs text-zinc-500 hover:text-zinc-800"
        >
          Nicht {session.user.name}? Abmelden
        </button>
      ) : null}
    </div>
  );
}

export function AcceptInvitationPage() {
  const { invitationId } = useParams({ strict: false }) as { invitationId: string };
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [busy, setBusy] = useState(false);
  const invitation = useQuery({
    queryKey: ["invitation", invitationId],
    queryFn: async () => {
      const { data, error } = await authClient.organization.getInvitation({
        query: { id: invitationId },
      });
      if (error) throw error;
      return data;
    },
    retry: false,
  });

  async function accept() {
    setBusy(true);
    const { data, error } = await authClient.organization.acceptInvitation({ invitationId });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Einladung konnte nicht angenommen werden");
      return;
    }
    await queryClient.invalidateQueries();
    const orgId = data?.member.organizationId ?? invitation.data?.organizationId;
    if (orgId) {
      rememberOrg(orgId);
      await navigate({ to: "/o/$orgId", params: { orgId } });
    }
  }

  if (invitation.isLoading)
    return <p className="text-center text-sm text-zinc-500">Einladung wird geladen …</p>;
  if (invitation.isError || !invitation.data) {
    return (
      <div className="text-center">
        <h1 className="text-lg font-semibold">Einladung nicht gefunden</h1>
        <p className="mt-2 text-sm text-zinc-500">
          Die Einladung ist abgelaufen, wurde zurückgezogen oder gilt für eine andere E-Mail-Adresse
          {session ? ` als ${session.user.email}` : ""}.
        </p>
      </div>
    );
  }
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
        <Building2 className="size-6" />
      </div>
      <h1 className="text-xl font-semibold">Einladung zu „{invitation.data.organizationName}“</h1>
      <p className="mt-2 text-sm text-zinc-500">
        {invitation.data.inviterEmail} hat dich als <strong>{invitation.data.role}</strong>{" "}
        eingeladen.
      </p>
      <Button className="mt-6 w-full" loading={busy} onClick={accept}>
        Einladung annehmen
      </Button>
    </div>
  );
}

function slugify(value: string): string {
  const base = value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `${base || "org"}-${Math.random().toString(36).slice(2, 6)}`;
}

export async function createOrganization(name: string): Promise<string | null> {
  const { data, error } = await authClient.organization.create({ name, slug: slugify(name) });
  if (error || !data) {
    toast.error(error?.message ?? "Organisation konnte nicht angelegt werden");
    return null;
  }
  await queryClient.invalidateQueries();
  rememberOrg(data.id);
  return data.id;
}

export function OnboardingPage() {
  const system = useQuery(orpc.system.info.queryOptions());
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const orgId = await createOrganization(name);
    setBusy(false);
    if (orgId) await navigate({ to: "/o/$orgId", params: { orgId } });
  }

  return (
    <>
      <h1 className="text-xl font-semibold">
        Willkommen{session ? `, ${session.user.name.split(" ")[0]}` : ""}!
      </h1>
      <p className="mt-1 mb-6 text-sm text-zinc-500">
        Lege deine kostenlose Organisation an. Free kostet dauerhaft 0 € und benötigt keine
        Kreditkarte. Mehr Speicher und Funktionen kannst du später freischalten.
      </p>
      {system.data?.emailVerificationRequired && session && !session.user.emailVerified ? (
        <div className="mb-4 rounded-lg bg-brand-50 p-3 text-sm">
          <p>
            Bitte bestätige deine E-Mail-Adresse, bevor du deine kostenlose Organisation anlegst.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={async () => {
              const { error } = await authClient.sendVerificationEmail({
                email: session.user.email,
                callbackURL: `${window.location.origin}/onboarding`,
              });
              if (error) toast.error(error.message ?? "E-Mail konnte nicht gesendet werden");
              else toast.success("Bestätigungslink wurde gesendet.");
            }}
          >
            Bestätigungslink erneut senden
          </Button>
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name der Organisation">
          <Input
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Muster GmbH"
          />
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Organisation anlegen
        </Button>
      </form>
    </>
  );
}
